import {
	type BlockState,
	type IArea,
	type ICollationCheck,
	type ILiveElectionSummary,
	type IStanding,
	type IStrongholdLive,
	type IWhyPending,
	type ProjectionGate,
	type ResultSourceName,
	type StrongholdLevel,
	type StrongholdStatus,
	type Verdict,
} from '../types';

/** The oldest summary contract this page understands. */
export const MIN_CONTRACT_MAJOR = 1;

/** Thrown when the body is not a summary this page can read. Classified as `outdated`. */
export class UnreadableSummaryError extends Error {
	constructor(reason: string) {
		super(`Live Election summary is unreadable: ${reason}`);
		this.name = 'UnreadableSummaryError';
	}
}

type Raw = Record<string, unknown>;

const isObj = (v: unknown): v is Raw => typeof v === 'object' && v !== null && !Array.isArray(v);

/**
 * A finite number, else null. The backend serializes unset fields as the literal string
 * "null"; that, undefined, '' and NaN are all "not sourced" — never 0.
 */
export const numOrNull = (v: unknown): number | null => {
	if (typeof v === 'number') return Number.isFinite(v) ? v : null;
	if (typeof v === 'string' && v.trim() !== '' && v.trim().toLowerCase() !== 'null') {
		const n = Number(v);
		return Number.isFinite(n) ? n : null;
	}
	return null;
};

/**
 * A COUNT whose absence genuinely sums nothing (a breakdown bucket, an excluded-sheet tally).
 * The one sanctioned zero default in this module — never use it for a measurement such as a
 * register or a margin, where absence means "not sourced".
 */
const countOrZero = (v: unknown): number => {
	const n = numOrNull(v);
	return n === null ? 0 : Math.max(0, n);
};

/** A count the contract REQUIRES. Missing means the body is not a summary. */
const requiredCount = (v: unknown, field: string): number => {
	const n = numOrNull(v);
	if (n === null) throw new UnreadableSummaryError(`${field} is missing`);
	return n;
};

const strOrNull = (v: unknown): string | null => {
	if (typeof v !== 'string') return null;
	const t = v.trim();
	return t === '' || t.toLowerCase() === 'null' || t.toLowerCase() === 'undefined' ? null : t;
};

const bool = (v: unknown): boolean => v === true || v === 'true';

const oneOf = <T extends string>(v: unknown, allowed: readonly T[], fallback: T): T =>
	typeof v === 'string' && (allowed as readonly string[]).includes(v) ? (v as T) : fallback;

const VERDICTS: readonly Verdict[] = ['NO_RESULTS', 'DECIDED', 'WITHHELD', 'TOO_CLOSE', 'LIKELY', 'IN_PLAY'];
const GATES: readonly ProjectionGate[] = ['FULL', 'AT_LEAST', 'WITHHELD'];
const LEVELS: readonly StrongholdLevel[] = ['Lga', 'Ward', 'PollingUnit'];
const STATUSES: readonly StrongholdStatus[] = ['FULLY_REPORTED', 'PARTIAL', 'NOT_REPORTED', 'EMPTY_ROSTER'];
const SOURCES: readonly ResultSourceName[] = ['Agent', 'Citizen', 'IRev', 'AI'];

/** api.query bodies arrive bare or in the house envelope; accept both, once, here. */
export const unwrapEnvelope = (body: unknown): unknown =>
	isObj(body) && isObj(body.data) && ('isSuccessful' in body || 'statusCode' in body) ? body.data : body;

/**
 * A block the server may predate. A missing KEY is `absent` ("not on this server"); a
 * present array — even empty — is a value ("none declared").
 */
const block = <T>(raw: Raw, key: string, map: (item: Raw) => T): BlockState<T[]> => {
	if (!(key in raw) || raw[key] === null || raw[key] === undefined) return { state: 'absent' };
	const list = raw[key];
	if (!Array.isArray(list)) return { state: 'absent' };
	return { state: 'value', value: list.filter(isObj).map(map) };
};

const standing = (s: Raw): IStanding => ({
	foldKey: strOrNull(s.foldKey) ?? '',
	displayName: strOrNull(s.displayName) ?? 'Unnamed party',
	votes: countOrZero(s.votes),
	share: numOrNull(s.share),
	rank: numOrNull(s.rank) ?? Number.MAX_SAFE_INTEGER,
	isViewer: bool(s.isViewer),
});

const whyPending = (w: unknown): IWhyPending | null => {
	if (!isObj(w)) return null;
	const count = (k: string) => countOrZero(w[k]);
	return {
		noLedgerRow: count('noLedgerRow'),
		noApplicant: count('noApplicant'),
		applicantNotAssigned: count('applicantNotAssigned'),
		assignedNotUploaded: count('assignedNotUploaded'),
		uploadedNotInResults: count('uploadedNotInResults'),
	};
};

const area = (a: Raw): IArea => ({
	level: a.level === 'ward' ? 'ward' : 'lga',
	code: strOrNull(a.code) ?? '',
	name: strOrNull(a.name) ?? strOrNull(a.code) ?? '',
	parentCode: strOrNull(a.parentCode),
	totalPollingUnits: numOrNull(a.totalPollingUnits),
	reportedPollingUnits: countOrZero(a.reportedPollingUnits),
	registeredPending: numOrNull(a.registeredPending),
	registeredPendingIsLowerBound: bool(a.registeredPendingIsLowerBound),
	leaderKey: strOrNull(a.leaderKey),
	viewerVotes: numOrNull(a.viewerVotes),
	viewerMargin: numOrNull(a.viewerMargin),
	isStronghold: bool(a.isStronghold),
});

const stronghold = (s: Raw): IStrongholdLive => ({
	id: numOrNull(s.id) ?? -1,
	level: oneOf(s.level, LEVELS, 'Ward'),
	code: strOrNull(s.code) ?? '',
	name: strOrNull(s.name) ?? strOrNull(s.code) ?? '',
	label: strOrNull(s.label),
	status: oneOf(s.status, STATUSES, 'NOT_REPORTED'),
	totalPollingUnits: numOrNull(s.totalPollingUnits),
	reportedPollingUnits: countOrZero(s.reportedPollingUnits),
	registeredPending: numOrNull(s.registeredPending),
	registeredPendingIsLowerBound: bool(s.registeredPendingIsLowerBound),
	viewerMargin: numOrNull(s.viewerMargin),
	whyPending: whyPending(s.whyPending),
	incidents: isObj(s.incidents) ? { open: countOrZero(s.incidents.open), total: countOrZero(s.incidents.total) } : null,
	declaredAfterFirstReport: bool(s.declaredAfterFirstReport),
});

const collation = (c: Raw): ICollationCheck => ({
	level: oneOf(c.level, ['ward', 'lga', 'state'] as const, 'ward'),
	code: strOrNull(c.code) ?? '',
	name: strOrNull(c.name) ?? strOrNull(c.code) ?? '',
	delta: numOrNull(c.delta),
	partyKey: strOrNull(c.partyKey),
	checkable: bool(c.checkable),
	unitsMissing: numOrNull(c.unitsMissing),
});

/**
 * Raw response → summary. Throws {@link UnreadableSummaryError} for a body that is not a
 * summary at all (no contract, wrong major, missing required counts) — a 200 that cannot
 * be read is an error, never an empty page.
 */
export const normalizeSummary = (body: unknown): ILiveElectionSummary => {
	const raw = unwrapEnvelope(body);
	if (!isObj(raw)) throw new UnreadableSummaryError('body is not an object');

	const contractVersion = strOrNull(raw.contractVersion);
	if (!contractVersion) throw new UnreadableSummaryError('contractVersion is missing');
	const major = Number(contractVersion.split('/').pop()?.split('.')[0]);
	if (!Number.isFinite(major) || major < MIN_CONTRACT_MAJOR) {
		throw new UnreadableSummaryError(`contract ${contractVersion} is older than this page`);
	}

	const coverage = isObj(raw.coverage) ? raw.coverage : {};
	const outstanding = isObj(raw.outstanding) ? raw.outstanding : {};
	const outcome = isObj(raw.outcome) ? raw.outcome : {};
	const caps = isObj(raw.capabilities) ? raw.capabilities : {};
	const lead = isObj(raw.lead) ? raw.lead : null;
	const viewer = isObj(raw.viewer) ? raw.viewer : null;
	const quality = isObj(raw.dataQuality) ? raw.dataQuality : null;
	const unnamed = isObj(raw.unnamed) ? raw.unnamed : null;
	const cancelled = isObj(raw.cancelledUnits) ? raw.cancelledUnits : null;
	const cancelledCount = cancelled ? numOrNull(cancelled.count) : null;

	const standings = (Array.isArray(raw.standings) ? raw.standings : []).filter(isObj).map(standing);
	standings.sort((a, b) => a.rank - b.rank || b.votes - a.votes || a.foldKey.localeCompare(b.foldKey));

	return {
		contractVersion,
		asOf: strOrNull(raw.asOf),
		latestReportAt: strOrNull(raw.latestReportAt),
		capabilities: {
			liveEvents: bool(caps.liveEvents),
			collation: bool(caps.collation),
			strongholds: bool(caps.strongholds),
		},
		sourcesUsed: (Array.isArray(raw.sourcesUsed) ? raw.sourcesUsed : []).filter((s): s is ResultSourceName =>
			(SOURCES as readonly unknown[]).includes(s)
		),
		coverage: {
			totalPollingUnits: numOrNull(coverage.totalPollingUnits),
			reportedPollingUnits: requiredCount(coverage.reportedPollingUnits, 'coverage.reportedPollingUnits'),
			pendingPollingUnits: numOrNull(coverage.pendingPollingUnits),
			reportedRatio: numOrNull(coverage.reportedRatio),
		},
		standings,
		unnamed: unnamed ? { votes: countOrZero(unnamed.votes), rows: countOrZero(unnamed.rows) } : null,
		lead: lead
			? {
					leaderKey: strOrNull(lead.leaderKey) ?? '',
					runnerUpKey: strOrNull(lead.runnerUpKey),
					margin: requiredCount(lead.margin, 'lead.margin'),
					marginShare: numOrNull(lead.marginShare),
					tie: bool(lead.tie),
				}
			: null,
		viewer: viewer
			? {
					foldKey: strOrNull(viewer.foldKey),
					matchedInResults: bool(viewer.matchedInResults),
					rank: numOrNull(viewer.rank),
					votes: numOrNull(viewer.votes),
					gapToLeader: numOrNull(viewer.gapToLeader),
					votesNeeded: numOrNull(viewer.votesNeeded),
					requiredShareOfOutstanding: numOrNull(viewer.requiredShareOfOutstanding),
					mathematicallyEliminated: bool(viewer.mathematicallyEliminated),
				}
			: null,
		outstanding: {
			registeredPending: numOrNull(outstanding.registeredPending),
			pendingCaptureRatio: numOrNull(outstanding.pendingCaptureRatio),
			pendingUncapturedUnits: numOrNull(outstanding.pendingUncapturedUnits),
			registerComplete: bool(outstanding.registerComplete),
			projected: numOrNull(outstanding.projected),
			projectionGate: oneOf(outstanding.projectionGate, GATES, 'WITHHELD'),
			withheldReason: strOrNull(outstanding.withheldReason),
		},
		outcome: {
			// An unknown verdict is withheld, never promoted to a claim.
			verdict: oneOf(outcome.verdict, VERDICTS, 'WITHHELD'),
			basis: strOrNull(outcome.basis) ?? '',
			reason: strOrNull(outcome.reason),
		},
		areas: (Array.isArray(raw.areas) ? raw.areas : []).filter(isObj).map(area),
		strongholds: block(raw, 'strongholds', stronghold),
		collation: block(raw, 'collation', collation),
		dataQuality: quality
			? {
					firstSubmissionOnly: bool(quality.firstSubmissionOnly),
					excludedSheets: countOrZero(quality.excludedSheets),
					resultsOutsideRoster: countOrZero(quality.resultsOutsideRoster),
					unnamedVotes: countOrZero(quality.unnamedVotes),
					notes: (Array.isArray(quality.notes) ? quality.notes : []).filter((n): n is string => typeof n === 'string'),
				}
			: null,
		cancelledUnits:
			cancelled && cancelledCount !== null
				? {
						count: cancelledCount,
						unreviewed: numOrNull(cancelled.unreviewed),
						withSubmittedResults: numOrNull(cancelled.withSubmittedResults),
						votesInCancelledUnits: numOrNull(cancelled.votesInCancelledUnits),
					}
				: null,
	};
};
